import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, fmtDate, initials } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { ClientSearch } from "@/components/common/ClientSearch";
import { ImportWizard } from "@/components/common/ImportWizard";
import { DuplicatesDialog } from "@/components/common/DuplicatesDialog";
import { hasPerm, ACQUISITION_SOURCES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Search, ChevronRight, Upload, Loader2, GitMerge, Users } from "lucide-react";
import { PhoneField } from "@/components/common/PhoneField";

export default function Clienti() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canImport = hasPerm(user, "clients.write");
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", birthdate: "", notes: "", acquisition_source: "", referrer_id: "", referrer_name_raw: "" });
  const [referrerClient, setReferrerClient] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mPrimary, setMPrimary] = useState(null);
  const [mSecondary, setMSecondary] = useState(null);
  const [mPreview, setMPreview] = useState(null);
  const [mBusy, setMBusy] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);

  const loadDupCount = () => {
    if (!isAdmin) return;
    api.get("/clients/duplicates").then((r) => setDupCount(r.data.total_profiles || 0)).catch(() => {});
  };
  useEffect(() => { loadDupCount(); /* eslint-disable-next-line */ }, [isAdmin]);

  useEffect(() => {
    if (mPrimary && mSecondary && mPrimary.id !== mSecondary.id) {
      api.get(`/clients/${mPrimary.id}/merge-preview?secondary_id=${mSecondary.id}`)
        .then((r) => setMPreview(r.data)).catch(() => setMPreview(null));
    } else { setMPreview(null); }
  }, [mPrimary, mSecondary]);

  const doMerge = async () => {
    if (!mPrimary || !mSecondary) return;
    if (mPrimary.id === mSecondary.id) { toast.error("Seleziona due profili diversi"); return; }
    setMBusy(true);
    try {
      await api.post(`/clients/${mPrimary.id}/merge`, { secondary_id: mSecondary.id });
      toast.success("Profili uniti");
      setMergeOpen(false); setMPrimary(null); setMSecondary(null); setMPreview(null);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore unione"); }
    finally { setMBusy(false); }
  };

  const load = (query = "") => api.get(`/clients${query ? `?q=${query}` : ""}`).then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const create = async () => {
    if (!form.first_name) { toast.error("Inserisci il nome"); return; }
    if (form.acquisition_source === "amica" && !referrerClient && !form.referrer_name_raw.trim()) {
      toast.error("Indica quale amica l'ha presentata (seleziona una cliente o scrivi il nome)"); return;
    }
    const payload = { ...form, phone: form.phone, referrer_id: referrerClient?.id || "", referrer_name_raw: form.referrer_name_raw?.trim() || "" };
    try {
      const { data } = await api.post("/clients", payload);
      toast.success("Cliente creato");
      setOpen(false);
      setForm({ first_name: "", last_name: "", phone: "", email: "", birthdate: "", notes: "", acquisition_source: "", referrer_id: "", referrer_name_raw: "" });
      setReferrerClient(null);
      nav(`/clienti/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione cliente"); }
  };

  return (
    <div>
      <PageHeader title="Clienti" subtitle={`${clients.length} clienti registrati`}>
        {canImport && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="client-import-button"><Upload className="h-4 w-4 mr-1" /> Importa</Button>}
        {isAdmin && (
          <Button variant={dupCount > 0 ? "default" : "outline"} onClick={() => setDupOpen(true)} data-testid="client-duplicates-button">
            <Users className="h-4 w-4 mr-1" /> Da unificare
            {dupCount > 0 && <Badge className="ml-2 bg-white/25 text-white border-0 px-1.5">{dupCount}</Badge>}
          </Button>
        )}
        {isAdmin && <Button variant="outline" onClick={() => setMergeOpen(true)} data-testid="client-merge-button"><GitMerge className="h-4 w-4 mr-1" /> Unione manuale</Button>}
        <Button onClick={() => setOpen(true)} data-testid="client-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo cliente</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono, email..." className="pl-9" data-testid="client-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {clients.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground col-span-full">Nessun cliente trovato.</Card>}
        {clients.map((c) => (
          <Card key={c.id} onClick={() => nav(`/clienti/${c.id}`)} data-testid={`client-card-${c.id}`}
            className="p-4 cursor-pointer hover:bg-accent/40 flex items-center gap-3">
            <Avatar className="h-11 w-11"><AvatarFallback className="bg-[hsl(var(--hi-gold-soft))] text-[hsl(var(--hi-gold))] font-semibold">{initials(`${c.first_name} ${c.last_name}`)}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.first_name} {c.last_name}</div>
              <div className="text-xs text-muted-foreground truncate">{c.phone || c.email || "—"}</div>
              <div className="flex gap-1 mt-1">{(c.tags||[]).map((t) => <Badge key={t} variant="secondary" className="text-[10px] px-1.5 py-0">{t}</Badge>)}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Card>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-2xl">Nuovo cliente</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nome</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="client-first-name" /></div>
              <div className="space-y-1.5"><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="client-last-name" /></div>
            </div>
            <div className="space-y-1.5"><Label>Telefono</Label>
              <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="client-phone" />
              <p className="text-[11px] text-muted-foreground">Prefisso predefinito 🇮🇹 +39. Il numero viene salvato in formato internazionale (es. +393331234567).</p>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
            <div className="space-y-1.5"><Label>Data di nascita <span className="text-muted-foreground font-normal">(facoltativa)</span></Label><Input type="date" value={form.birthdate || ""} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} data-testid="client-birthdate" /></div>
            <div className="space-y-1.5">
              <Label>Come ci hai conosciuti? <span className="text-muted-foreground font-normal">(facoltativo)</span></Label>
              <Select value={form.acquisition_source} onValueChange={(v) => { setForm({ ...form, acquisition_source: v }); if (v !== "amica") setReferrerClient(null); }}>
                <SelectTrigger data-testid="client-acquisition-source"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {ACQUISITION_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.acquisition_source === "amica" && (
              <div className="space-y-2 rounded-lg border border-[hsl(var(--hi-gold)/0.4)] p-3 bg-[hsl(var(--hi-gold)/0.06)]" data-testid="client-referrer-block">
                <Label className="text-xs">Quale amica l'ha presentata? (cliente esistente)</Label>
                <ClientSearch value={referrerClient} onSelect={setReferrerClient} />
                <div className="text-[11px] text-muted-foreground">Non la trovi? Scrivi il nome: verrà segnata come "da confermare".</div>
                <Input value={form.referrer_name_raw} onChange={(e) => setForm({ ...form, referrer_name_raw: e.target.value })} placeholder="Nome amica (se non in elenco)" data-testid="client-referrer-name-raw" className="h-8 text-sm" disabled={!!referrerClient} />
              </div>
            )}
            <div className="space-y-1.5"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button className="w-full" onClick={create} data-testid="client-save">Salva cliente</Button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto hi-scroll" data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Importa da altro gestionale</DialogTitle></DialogHeader>
          <ImportWizard defaultEntity="clients" allowEntitySwitch onDone={() => load(q)} />
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={(o) => { setMergeOpen(o); if (!o) { setMPrimary(null); setMSecondary(null); setMPreview(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto hi-scroll" data-testid="client-merge-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Unisci profili duplicati</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Tutti gli appuntamenti, vendite, pagamenti e lo storico del profilo da unire verranno spostati sul profilo da mantenere. L'operazione \u00e8 tracciata.</p>
            <div className="space-y-1.5"><Label>Profilo da mantenere</Label>
              <ClientSearch value={mPrimary} onSelect={setMPrimary} />
            </div>
            <div className="space-y-1.5"><Label>Profilo da unire (verr\u00e0 archiviato)</Label>
              <ClientSearch value={mSecondary} onSelect={setMSecondary} />
            </div>
            {mPreview && (
              <div className="rounded-md border border-border p-3 text-sm" data-testid="merge-preview">
                <div className="font-medium mb-1">Dati che verranno spostati</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                  {Object.entries(mPreview.counts).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium text-foreground">{v}</span></div>
                  ))}
                  {Object.values(mPreview.counts).every((v) => v === 0) && <div>Nessun dato collegato al profilo da unire</div>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Annulla</Button>
            <Button onClick={doMerge} disabled={mBusy || !mPrimary || !mSecondary || (mPrimary && mSecondary && mPrimary.id === mSecondary.id)} data-testid="merge-confirm-btn">
              {mBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Unisci profili
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onChanged={() => { load(q); loadDupCount(); }} />
    </div>
  );
}
